import { isOwnerOrOrgAdmin } from '@helpers/roleScope';
import {
  FineType,
  MemberTransactionType,
  UpdateDueOptionsInterface
} from '@types';

export const isDevEnvironment = process.env.REACT_APP_ENV === 'development';

export const extractPaymentLinkUrl = (message: string): string => {
  const [url] = message.match(/https?:\/\/\S+/) ?? [];
  return url ?? message;
};

export const mapRowToUpdateDueOptions = (
  row: MemberTransactionType
): UpdateDueOptionsInterface => ({
  id: row.id,
  total_fee: row.total_amount,
  total_paid: row.total_paid_amount,
  action: Number(row.total_paid_amount) > 0 ? 'close_payment' : 'discount',
  name: row.name,
  original_amount_requested: row.original_amount_requested,
  fine_amount: row.fine_amount,
  fine_type: row.fine_type,
  fee_amount_configuration: row.fee_amount_configuration,
  total_gst: row.total_gst,
  cgst_percentage: row.cgst_percentage,
  sgst_percentage: row.sgst_percentage,
  igst_percentage: row.igst_percentage,
  total_discount_amount: row.total_discount_amount,
  original_total_amount_requested: row.original_total_amount_requested
});

export type PaymentOrderActionAccess = {
  canMarkAsPaid: boolean;
  canCreateDiscount: boolean;
  canClosePayment: boolean;
  canEnablePartialPayment: boolean;
  canDisable: boolean;
  canSendReminder: boolean;
};

/**
 * Update Due Amount CTA opens a modal with Discount and/or Close Payment.
 * Close is only usable when the order already has a paid amount (same rule as
 * the disabled Close radio in UpdateDueModal). Show the CTA only when at least
 * one of those actions is actually available for this row.
 */
export const canShowUpdateDueAmount = ({
  canCreateDiscount,
  canClosePayment,
  enableDiscountInUpdateDueModal,
  totalPaidAmount,
  fineType
}: {
  canCreateDiscount: boolean;
  canClosePayment: boolean;
  enableDiscountInUpdateDueModal: boolean;
  totalPaidAmount: string | number;
  fineType?: FineType;
}): boolean => {
  if (!enableDiscountInUpdateDueModal) return false;

  const totalPaid = Number(totalPaidAmount);
  const canUseClosePayment = canClosePayment && totalPaid > 0;
  const canUseDiscount = canCreateDiscount && fineType !== 'percentage';

  return canUseClosePayment || canUseDiscount;
};

type TransactionRow = MemberTransactionType & {
  is_custom_term?: boolean;
  remind_again?: boolean;
  reminder_count_exceeded?: boolean;
  payment_link?: string;
};

type TransactionRowActionHandlers = {
  onClickMarkAsPaid: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void;
  onClickUpdateDue: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void;
  onClickClosePayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void;
  onClickSendReminder: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void;
  handleCopy: (
    e: React.MouseEvent,
    link: string,
    row: MemberTransactionType
  ) => void;
  onClickEnablePartialPayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void;
  onClickDisable: (event: React.MouseEvent, row: MemberTransactionType) => void;
};

type TransactionRowActionOptions = {
  access: PaymentOrderActionAccess;
  enableDiscountInUpdateDueModal: boolean;
  splitRequiredMinAmount: string;
  isAutoPayInProgress?: boolean;
  isRestrictDisablePaymentOrderEnabled?: boolean;
  userRole?: string;
  isOrgLite?: boolean;
  /** When true, copy payment link respects autopay disabled state (all tab). */
  restrictCopyLinkOnAutoPay?: boolean;
};

export const getTransactionRowActions = (
  row: TransactionRow,
  handlers: TransactionRowActionHandlers,
  options: TransactionRowActionOptions
) => {
  const {
    onClickMarkAsPaid,
    onClickUpdateDue,
    onClickClosePayment,
    onClickSendReminder,
    handleCopy,
    onClickEnablePartialPayment,
    onClickDisable
  } = handlers;

  const {
    access,
    enableDiscountInUpdateDueModal,
    splitRequiredMinAmount,
    isAutoPayInProgress,
    isRestrictDisablePaymentOrderEnabled,
    userRole,
    isOrgLite,
    restrictCopyLinkOnAutoPay = false
  } = options;

  const {
    canMarkAsPaid,
    canCreateDiscount,
    canClosePayment,
    canEnablePartialPayment,
    canDisable,
    canSendReminder
  } = access;

  const isOwnerOrOrgAdminRole = isOwnerOrOrgAdmin({
    role_scope: userRole,
    role: userRole
  });

  const showDisableIcon = (item: MemberTransactionType) =>
    item?.status === 'processing' &&
    item.total_paid_amount === '0' &&
    canDisable &&
    (!isRestrictDisablePaymentOrderEnabled || isOwnerOrOrgAdminRole);

  const showEnablePartialPayment =
    canEnablePartialPayment &&
    !row.is_split_payment &&
    (row.is_custom_term ||
      Number(row.total_amount) >= Number(splitRequiredMinAmount));

  const showToSendReminder =
    canSendReminder &&
    (row.remind_again || (row.reminder_count_exceeded && !row.remind_again));
  const showReminderAlreadySent =
    canSendReminder && !row.remind_again && !row.reminder_count_exceeded;

  const closePaymentAccess =
    canClosePayment && row.is_split_payment && row.total_paid_amount !== '0';

  const canUpdateDue = canShowUpdateDueAmount({
    canCreateDiscount,
    canClosePayment,
    enableDiscountInUpdateDueModal,
    totalPaidAmount: row.total_paid_amount,
    fineType: row.fine_type
  });

  const disabledTooltip = (item: MemberTransactionType) => {
    if (isAutoPayInProgress) return 'Autopay in progress';
    if (item.reminder_count_exceeded)
      return 'Reminder cannot be sent as the maximum reminder limit has been reached.\nPlease share the payment link manually';
    return undefined;
  };

  const showCopyPaymentLink = canSendReminder && Boolean(row?.payment_link);

  const copyLinkAction = showCopyPaymentLink
    ? {
        id: `copyPaymentLink-${row.id}`,
        label: isOrgLite ? 'Copy Payment Message' : 'Copy Payment Link',
        onClick: (event: React.MouseEvent) =>
          handleCopy(event, row?.payment_link ?? '', row),
        iconName: 'copy-primary',
        tooltipText: 'Copied!',
        ...(restrictCopyLinkOnAutoPay && {
          isDisabled: row.status === 'disabled' || isAutoPayInProgress,
          disabledTooltip: isAutoPayInProgress
            ? 'Autopay in progress'
            : undefined
        })
      }
    : undefined;

  const copyLinkOnlyAction =
    showCopyPaymentLink && isDevEnvironment
      ? {
          id: `copyLinkOnly-${row.id}`,
          label: 'Copy Link Only',
          onClick: (event: React.MouseEvent) =>
            handleCopy(
              event,
              extractPaymentLinkUrl(row?.payment_link ?? ''),
              row
            ),
          iconName: 'copy-primary',
          tooltipText: 'Copied!',
          ...(restrictCopyLinkOnAutoPay && {
            isDisabled: row.status === 'disabled' || isAutoPayInProgress,
            disabledTooltip: isAutoPayInProgress
              ? 'Autopay in progress'
              : undefined
          })
        }
      : undefined;

  const openLinkAction =
    showCopyPaymentLink && isDevEnvironment
      ? {
          id: `openPaymentLink-${row.id}`,
          label: 'Open Link',
          onClick: (event: React.MouseEvent) => {
            event.stopPropagation();
            window.open(
              extractPaymentLinkUrl(row?.payment_link ?? ''),
              '_blank',
              'noopener,noreferrer'
            );
          },
          iconName: 'link',
          ...(restrictCopyLinkOnAutoPay && {
            isDisabled: row.status === 'disabled' || isAutoPayInProgress,
            disabledTooltip: isAutoPayInProgress
              ? 'Autopay in progress'
              : undefined
          })
        }
      : undefined;

  const actions = [
    {
      ...(canMarkAsPaid && {
        id: `markAsPaid-${row.id}`,
        label: 'Mark As Paid',
        onClick: (event: React.MouseEvent) => onClickMarkAsPaid(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress
          ? 'Autopay in progress'
          : undefined,
        style: 'text-primary'
      })
    },
    {
      ...(canUpdateDue && {
        id: `updateDue-${row.id}`,
        label: 'Update Due Amount',
        onClick: (event: React.MouseEvent) => onClickUpdateDue(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress ? 'Autopay in progress' : undefined
      })
    },
    {
      ...(closePaymentAccess &&
        !enableDiscountInUpdateDueModal && {
          id: `closePayment-${row.id}`,
          label: 'Close Payment',
          onClick: (event: React.MouseEvent) => onClickClosePayment(event, row),
          isDisabled: row.status === 'disabled' || isAutoPayInProgress,
          disabledTooltip: isAutoPayInProgress
            ? 'Autopay in progress'
            : undefined,
          style: 'text-error40'
        })
    },
    {
      ...(showToSendReminder && {
        id: `sendReminder-${row.id}`,
        label: 'Send Reminder',
        onClick: (event: React.MouseEvent) => onClickSendReminder(event, row),
        isDisabled:
          row.status === 'disabled' ||
          isAutoPayInProgress ||
          row.reminder_count_exceeded,
        disabledTooltip: disabledTooltip(row),
        showWarningIcon: !!row.reminder_count_exceeded
      })
    },
    {
      ...(showReminderAlreadySent && {
        id: `reminderSent-${row.id}`,
        label: 'Reminder Sent',
        onClick: (event: React.MouseEvent) =>
          handleCopy(event, row?.payment_link ?? '', row),
        isDisabled: true
      })
    },
    {
      ...(showEnablePartialPayment && {
        id: `enablePartialPayment-${row.id}`,
        label: 'Enable Partial Payment',
        onClick: (event: React.MouseEvent) =>
          onClickEnablePartialPayment(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress
          ? 'Autopay in progress'
          : undefined,
        style: 'text-yellow60'
      })
    },
    {
      ...(copyLinkAction && copyLinkAction)
    },
    {
      ...(copyLinkOnlyAction && copyLinkOnlyAction)
    },
    {
      ...(openLinkAction && openLinkAction)
    },
    {
      ...(showDisableIcon(row) && {
        id: `disable-${row.id}`,
        label: 'Disable',
        onClick: (event: React.MouseEvent) => onClickDisable(event, row),
        isDisabled: row.status === 'disabled' || isAutoPayInProgress,
        disabledTooltip: isAutoPayInProgress
          ? 'Autopay in progress'
          : undefined,
        style: 'text-error40'
      })
    }
  ];

  const hasAnyPaymentAction =
    canMarkAsPaid ||
    canUpdateDue ||
    (closePaymentAccess && !enableDiscountInUpdateDueModal) ||
    showToSendReminder ||
    showReminderAlreadySent ||
    showEnablePartialPayment ||
    showCopyPaymentLink ||
    showDisableIcon(row);

  const isActionButtonDisabled =
    row.status === 'disabled' || !hasAnyPaymentAction;

  return { actions, isActionButtonDisabled };
};

export const getAllTransactionTabActions = (
  row: TransactionRow,
  access: PaymentOrderActionAccess,
  enableDiscountInUpdateDueModal: boolean,
  onClickMarkAsPaid: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickUpdateDue: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickSendReminder: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickClosePayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  handleCopy: (
    e: React.MouseEvent,
    link: string,
    row: MemberTransactionType
  ) => void,
  splitRequiredMinAmount: string,
  onClickEnablePartialPayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickDisable: (event: React.MouseEvent, row: MemberTransactionType) => void,
  isAutoPayInProgress?: boolean,
  isRestrictDisablePaymentOrderEnabled?: boolean,
  userRole?: string,
  isOrgLite?: boolean
) =>
  getTransactionRowActions(
    row,
    {
      onClickMarkAsPaid,
      onClickUpdateDue,
      onClickClosePayment,
      onClickSendReminder,
      handleCopy,
      onClickEnablePartialPayment,
      onClickDisable
    },
    {
      access,
      enableDiscountInUpdateDueModal,
      splitRequiredMinAmount,
      isAutoPayInProgress,
      isRestrictDisablePaymentOrderEnabled,
      userRole,
      isOrgLite,
      restrictCopyLinkOnAutoPay: true
    }
  );

export const getPendingTransactionTabActions = (
  row: TransactionRow,
  access: PaymentOrderActionAccess,
  enableDiscountInUpdateDueModal: boolean,
  onClickMarkAsPaid: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickUpdateDue: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickClosePayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickSendReminder: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  handleCopy: (
    e: React.MouseEvent,
    link: string,
    row: MemberTransactionType
  ) => void,
  splitRequiredMinAmount: string,
  onClickEnablePartialPayment: (
    event: React.MouseEvent,
    row: MemberTransactionType
  ) => void,
  onClickDisable: (event: React.MouseEvent, row: MemberTransactionType) => void,
  isAutoPayInProgress?: boolean,
  isRestrictDisablePaymentOrderEnabled?: boolean,
  userRole?: string,
  isOrgLite?: boolean
) =>
  getTransactionRowActions(
    row,
    {
      onClickMarkAsPaid,
      onClickUpdateDue,
      onClickClosePayment,
      onClickSendReminder,
      handleCopy,
      onClickEnablePartialPayment,
      onClickDisable
    },
    {
      access,
      enableDiscountInUpdateDueModal,
      splitRequiredMinAmount,
      isAutoPayInProgress,
      isRestrictDisablePaymentOrderEnabled,
      userRole,
      isOrgLite,
      restrictCopyLinkOnAutoPay: false
    }
  );
